import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import posthog from "posthog-js";
import API_BASE from "../config";
import { clearExitGridSession } from "../components/terminal/useExitGrid";

const decodeToken = (token) => {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

const isTokenExpired = (decoded) => {
  if (!decoded?.exp) return false;
  return Date.now() >= decoded.exp * 1000;
};


function getInitialAuthState() {
  const empty = {
    userId: null,
    userEmail: null,
    userRole: null,
    pilotWhitelisted: false,
    betaTestingFlag: false,
    analyticsOptOut: false,
    isAuthenticated: false,
  };
  if (typeof window === "undefined") {
    return empty;
  }
  const token = localStorage.getItem("token");
  const storedUserId = localStorage.getItem("userId");
  if (!token) {
    return empty;
  }
  const decoded = decodeToken(token);
  if (!decoded || isTokenExpired(decoded)) {
    return empty;
  }
  return {
    userId: decoded.sub || storedUserId,
    userEmail: decoded.email || null,
    userRole: decoded.role || "user",
    pilotWhitelisted: !!decoded.pilot_whitelisted,
    betaTestingFlag: !!decoded.beta_testing_flag,
    analyticsOptOut: !!decoded.analytics_opt_out,
    isAuthenticated: true,
  };
}

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authState, setAuthState] = useState(getInitialAuthState);
  const { userId, userEmail, userRole, pilotWhitelisted, betaTestingFlag, analyticsOptOut, isAuthenticated } =
    authState;

  const setUserId = useCallback((v) => setAuthState((s) => ({ ...s, userId: v })), []);
  const setPilotWhitelisted = useCallback((v) => setAuthState((s) => ({ ...s, pilotWhitelisted: v })), []);

  const refetchMe = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const d = res.data;
        setAuthState((s) => ({
          ...s,
          userId: String(d.user_id ?? s.userId),
          userEmail: d.email ?? s.userEmail,
          userRole: d.role ?? s.userRole,
          pilotWhitelisted: !!d.pilot_whitelisted,
          betaTestingFlag: !!d.beta_testing_flag,
          analyticsOptOut: !!d.analytics_opt_out,
          isAuthenticated: true,
        }));

        if (d.user_id) {
          posthog.identify(String(d.user_id), {
            email: d.email,
            name: localStorage.getItem("userName"),
            role: d.role,
            pilot_whitelisted: !!d.pilot_whitelisted,
            beta_testing_flag: !!d.beta_testing_flag,
            analytics_opt_out: !!d.analytics_opt_out,
          });
          // Respect per-user analytics opt-out set from admin panel
          if (d.analytics_opt_out) {
            posthog.opt_out_capturing();
          } else {
            posthog.opt_in_capturing();
          }
        }
      })
      .catch(() => {});
  }, []);

  // Single token sync on mount (no /me yet)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserId = localStorage.getItem("userId");
    if (token) {
      const decoded = decodeToken(token);
      if (decoded) {
        if (isTokenExpired(decoded)) {
          localStorage.removeItem("token");
          localStorage.removeItem("userId");
          localStorage.removeItem("userName");
          localStorage.removeItem("userPic");
          setAuthState({
            userId: null,
            userEmail: null,
            userRole: null,
            pilotWhitelisted: false,
            betaTestingFlag: false,
            analyticsOptOut: false,
            isAuthenticated: false,
          });
        } else {
          setAuthState((s) => ({
            ...s,
            userId: decoded.sub || storedUserId,
            userEmail: decoded.email ?? s.userEmail,
            userRole: decoded.role || "user",
            pilotWhitelisted: !!decoded.pilot_whitelisted,
            betaTestingFlag: !!decoded.beta_testing_flag,
            analyticsOptOut: !!decoded.analytics_opt_out,
            isAuthenticated: true,
          }));
        }
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("userName");
        localStorage.removeItem("userPic");
        setAuthState({
          userId: null,
          userEmail: null,
          userRole: null,
          pilotWhitelisted: false,
          betaTestingFlag: false,
          analyticsOptOut: false,
          isAuthenticated: false,
        });
      }
    } else if (storedUserId) {
      localStorage.removeItem("userId");
      setAuthState({
        userId: null,
        userEmail: null,
        userRole: null,
        pilotWhitelisted: false,
        betaTestingFlag: false,
        analyticsOptOut: false,
        isAuthenticated: false,
      });
    } else {
      setAuthState({
        userId: null,
        userEmail: null,
        userRole: null,
        pilotWhitelisted: false,
        betaTestingFlag: false,
        analyticsOptOut: false,
        isAuthenticated: false,
      });
    }
  }, []);

  // Single /me fetch when token is present (runs once per app load)
  useEffect(() => {
    if (localStorage.getItem("token")) refetchMe();
  }, [refetchMe]);

  const logout = useCallback(() => {
    clearExitGridSession();
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("userPic");
    posthog.reset();
    setAuthState({
      userId: null,
      userEmail: null,
      userRole: null,
      pilotWhitelisted: false,
      betaTestingFlag: false,
      analyticsOptOut: false,
      isAuthenticated: false,
    });
    window.location.href = "/";
  }, []);

  const value = {
    userId,
    setUserId,
    userEmail,
    userRole,
    pilotWhitelisted,
    setPilotWhitelisted,
    betaTestingFlag,
    analyticsOptOut,
    isAuthenticated,
    isAdmin: () => userRole === "admin",
    hasRole: (role) => userRole === role,
    refetchMe,
    logout,
    token: isAuthenticated ? localStorage.getItem("token") : null,
    user: isAuthenticated && userId ? { id: userId, email: userEmail } : null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
